import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuTrash, LuTrash2 } from 'react-icons/lu';
import { Formik } from 'formik';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import sortBy from 'lodash/sortBy';
import { DateTime } from 'luxon';

import {
  Button,
  FormSelectField,
  FormTextField,
  FloatingCard,
  FormDateFromToField,
  FormCurrencyField,
} from '_components/_core';
import CustomFormSelectField from '_components/Transactions/components/CustomFormSelectField/CustomFormSelectField';
import { FormQuickDateField } from '_components/_shared';

import { CHARGE_TYPE_OPTIONS } from './utilities';
import { StyledFooter } from './styles';

const ChargeFilters = ({
  isOpen,
  recipients,
  paymentMethods,
  paymentGateways,
  chargeStatus,
  onToggle,
  onFilter,
}) => {
  const initialValues = {
    recipient_id: [],
    due_date_start: null,
    due_date_end: null,
    quick_date_due_date: null,
    payment_date_start: null,
    payment_date_end: null,
    quick_date_payment_date: null,
    amount_start: null,
    amount_end: null,
    charge_type: [],
    payment_method_id: [],
    description: null,
    created_at_start: null,
    created_at_end: null,
    quick_date_created_at: null,
    updated_at_start: null,
    updated_at_end: null,
    quick_date_updated_at: null,
    charge_status: [],
    company_payment_gateway_id: [],
    text: '',
  };

  const formattedRecipients = useMemo(() => {
    return [
      ...recipients.map(recipient => ({
        value: recipient.id,
        label: recipient.name,
      })),
    ];
  }, [recipients]);

  const formattedPaymentMethods = useMemo(() => {
    return [
      ...sortBy(paymentMethods, 'name').map(paymentMethod => ({
        value: paymentMethod.id,
        label: paymentMethod.name,
      })),
    ];
  }, [paymentMethods]);

  const formattedPaymentGateways = useMemo(() => {
    return [
      ...sortBy(paymentGateways, 'payment_gateway_name').map(paymentGateway => ({
        value: paymentGateway.id,
        label: paymentGateway.payment_gateway_name,
      })),
    ];
  }, [paymentGateways]);

  const formattedChargeStatus = useMemo(() => {
    return [
      ...sortBy(chargeStatus, 'sort_order').map(chargeStatus => ({
        value: chargeStatus.id,
        label: chargeStatus.description,
      })),
    ];
  }, [chargeStatus]);

  const handleFilter = useCallback(
    (values, withToggle = true) => {

      if (values.due_date_start) {
        values.due_date_start = DateTime.fromISO(values.due_date_start)
          .startOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }
      if (values.due_date_end) {
        values.due_date_end = DateTime.fromISO(values.due_date_end)
          .endOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }
      if (values.payment_date_start) {
        values.payment_date_start = DateTime.fromISO(values.payment_date_start)
          .startOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }
      if (values.payment_date_end) {
        values.payment_date_end = DateTime.fromISO(values.payment_date_end)
          .endOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }
      if (values.created_at_start) {
        values.created_at_start = DateTime.fromISO(values.created_at_start)
          .startOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }
      if (values.created_at_end) {
        values.created_at_end = DateTime.fromISO(values.created_at_end)
          .endOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }
      if (values.updated_at_start) {
        values.updated_at_start = DateTime.fromISO(values.updated_at_start)
          .startOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }
      if (values.updated_at_end) {
        values.updated_at_end = DateTime.fromISO(values.updated_at_end)
          .endOf('day')
          .toFormat('yyyy-MM-dd HH:mm:ss');
      }

      onFilter(values);

      if (withToggle) {
        onToggle();
      }
    },
    [onFilter, onToggle],
  );

  const handleClear = useCallback(
    (resetForm, setValues, withToggle = true) => {
      resetForm();
      setValues(initialValues);
      onFilter(initialValues);

      if (withToggle) {
        onToggle();
      }
    },
    [onFilter, onToggle],
  );

  return (
    <Formik initialValues={initialValues} onSubmit={values => handleFilter(values)}>
      {({ handleSubmit, resetForm, setValues, values }) => (
        <>
          <FloatingCard
            title="Filtrar cobranças"
            isVisible={isOpen}
            onToggleVisibility={() => {
              onToggle();
            }}
            footerContent={
              <StyledFooter>
                <div className="d-flex justify-content-center align-items-center">
                  <Button type="button" variant="success-2" onClick={handleSubmit}>
                    Filtrar cobranças
                  </Button>
                  <Button variant="link" className="m-0 p-0 ml-3" onClick={onToggle}>
                    Fechar
                  </Button>
                </div>
                <Button
                  variant="link"
                  className="m-0 p-0 d-flex justify-content-center align-items-center text-danger "
                  onClick={() => handleClear(resetForm, setValues)}
                >
                  <LuTrash size="1.2em" className="mr-2" />
                  Limpar filtros
                </Button>
              </StyledFooter>
            }
            side="right"
            fullHeight
            bodyClassName="p-0"
            withCloseButton
          >
            {isOpen && (
              <Form onSubmit={handleSubmit} className="mr-3 mt-3 ml-3">
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <FormDateFromToField
                      label="Data de vencimento"
                      fromName="due_date_start"
                      fromPlaceholder="De:"
                      fromId="due_date_start"
                      toName="due_date_end"
                      toPlaceholder="Até:"
                      toId="due_date_end"
                      inputContainerClassName="mt-2"
                      highlightFilled
                      fromStyle={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: 'none',
                      }}
                      leftContent={
                        <FormQuickDateField
                          isOptional
                          isChanged={false}
                          startDateFieldName="due_date_start"
                          endDateFieldName="due_date_end"
                          quickDateFieldName="quick_date_due_date"
                        />
                      }
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <FormDateFromToField
                      label="Data de recebimento"
                      fromName="payment_date_start"
                      fromPlaceholder="De:"
                      fromId="payment_date_start"
                      toName="payment_date_end"
                      toPlaceholder="Até:"
                      toId="payment_date_end"
                      inputContainerClassName="mt-2"
                      highlightFilled
                      fromStyle={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: 'none',
                      }}
                      leftContent={
                        <FormQuickDateField
                          isOptional
                          isChanged={false}
                          startDateFieldName="payment_date_start"
                          endDateFieldName="payment_date_end"
                          quickDateFieldName="quick_date_payment_date"
                        />
                      }
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <FormDateFromToField
                      label="Data de criação"
                      fromName="created_at_start"
                      fromPlaceholder="De:"
                      fromId="created_at_start"
                      toName="created_at_end"
                      toPlaceholder="Até:"
                      toId="created_at_end"
                      inputContainerClassName="mt-2"
                      highlightFilled
                      fromStyle={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: 'none',
                      }}
                      leftContent={
                        <FormQuickDateField
                          isOptional
                          isChanged={false}
                          startDateFieldName="created_at_start"
                          endDateFieldName="created_at_end"
                          quickDateFieldName="quick_date_created_at"
                        />
                      }
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <FormDateFromToField
                      label="Data de atualização"
                      fromName="updated_at_start"
                      fromPlaceholder="De:"
                      fromId="updated_at_start"
                      toName="updated_at_end"
                      toPlaceholder="Até:"
                      toId="updated_at_end"
                      inputContainerClassName="mt-2"
                      highlightFilled
                      fromStyle={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: 'none',
                      }}
                      leftContent={
                        <FormQuickDateField
                          isOptional
                          isChanged={false}
                          startDateFieldName="updated_at_start"
                          endDateFieldName="updated_at_end"
                          quickDateFieldName="quick_date_updated_at"
                        />
                      }
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <Form.Label>Status da cobrança</Form.Label>
                    <FormSelectField
                      name="charge_status"
                      options={formattedChargeStatus}
                      placeholder="Filtrar pelo status"
                      isClearable
                      highlightFilled
                      multiple
                      useValueForArrayOption
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <Form.Label>Tipo de cobrança</Form.Label>
                    <FormSelectField
                      name="charge_type"
                      options={CHARGE_TYPE_OPTIONS}
                      placeholder="Filtrar pelo tipo de cobrança"
                      isClearable
                      highlightFilled
                      multiple
                      useValueForArrayOption
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <Form.Label>Forma de pagamento</Form.Label>
                    <FormSelectField
                      name="payment_method_id"
                      options={formattedPaymentMethods}
                      placeholder="Filtrar pela forma de pagamento"
                      isClearable
                      highlightFilled
                      multiple
                      useValueForArrayOption
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <Form.Label>Contato/Cliente</Form.Label>
                    <CustomFormSelectField
                      name="recipient_id"
                      options={formattedRecipients}
                      placeholder="Filtrar pelo contato/cliente"
                      isClearable
                      metadata={{
                        type: null,
                      }}
                      loadingMessage={() => 'Carregando...'}
                      highlightFilled
                      width="100%"
                      multiple
                      useValueForArrayOption
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <Form.Label>Valor</Form.Label>
                    <div className="d-flex">
                      <FormCurrencyField
                        name="amount_start"
                        placeholder="De:"
                        highlightFilled
                        className="mr-2"
                      />
                      <FormCurrencyField
                        name="amount_end"
                        placeholder="Até:"
                        highlightFilled
                      />
                    </div>
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <Form.Label>Descrição</Form.Label>
                    <FormTextField
                      name="description"
                      placeholder="Filtrar pela descrição"
                      highlightFilled
                    />
                  </Form.Group>
                </Form.Row>
                <Form.Row>
                  <Form.Group as={Col} md="12">
                    <Form.Label>Provedor</Form.Label>
                    <FormSelectField
                      name="company_payment_gateway_id"
                      options={formattedPaymentGateways}
                      placeholder="Filtrar pelo provedor"
                      isClearable
                      highlightFilled
                      multiple
                      useValueForArrayOption
                    />
                  </Form.Group>
                </Form.Row>
              </Form>
            )}
          </FloatingCard>
          <Form
            onSubmit={e => {
              e.preventDefault();
              handleFilter(values, false);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleFilter(values, false);
              }
            }}
          >
            <Form.Row className="mb-0">
              <Form.Group
                as={Col}
                md="12"
                className="mb-0 d-flex align-items-center justify-content-center"
              >
                <FormTextField
                  name="text"
                  placeholder="Filtre por nome, razão social, cpf, cnpj, e-mail, telefone ou descrição da cobrança"
                  highlightFilled
                />
                {values.text && (
                  <LuTrash2
                    className="text-danger ml-2 cursor-pointer"
                    onClick={() => handleClear(resetForm, setValues, false)}
                  />
                )}
              </Form.Group>
            </Form.Row>
          </Form>
        </>
      )}
    </Formik>
  );
};

ChargeFilters.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
};

export default ChargeFilters;
